import { fetchOrders } from 'actions/OrderActions'
import { haveOrdersBeenFetched, sortOrdersByDeparture } from 'lib/order/orderUtils'
import moment from 'moment'
import type { Moment } from 'moment'
import { useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { getUpcomingOrders } from 'selectors/orderSelectors'
import { EmptyArray } from 'lib/array/arrayUtils'

/**
 * First non-cancelled hotel/bedbank stay on the order (earliest check-in wins per order).
 */
function getHotelStayRangeFromOrder(order: App.Order): { start: Moment, end: Moment } | undefined {
  const bedbank = order.bedbankItems.find(i => i.status !== 'cancelled')
  if (bedbank) {
    return {
      start: moment(bedbank.checkIn).startOf('day'),
      end: moment(bedbank.checkOut).startOf('day'),
    }
  }
  const item = order.items.find(
    i => i.status !== 'cancelled' && i.reservation?.startDate && i.reservation?.endDate,
  )
  if (item?.reservation?.startDate && item.reservation.endDate) {
    return {
      start: moment(item.reservation.startDate).startOf('day'),
      end: moment(item.reservation.endDate).startOf('day'),
    }
  }
  return undefined
}

export interface NextHotelBookingStay {
  checkIn: Moment
  checkOut: Moment
}

/**
 * Next upcoming hotel booking stay for the logged-in user (sorted by departure).
 * Fetches upcoming orders when needed. Returns undefined if not logged in or no hotel stay.
 */
function useNextHotelBookingStay(): NextHotelBookingStay | undefined {
  const dispatch = useAppDispatch()
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const ordersFetched = useAppSelector(state => haveOrdersBeenFetched(state, 'upcoming'))
  const upcomingOrders = useAppSelector((state: App.State) =>
    ordersFetched ? getUpcomingOrders(state) : EmptyArray,
  )

  useEffect(() => {
    if (isLoggedIn && !ordersFetched) {
      dispatch(fetchOrders({ status: 'upcoming' }))
    }
  }, [dispatch, isLoggedIn, ordersFetched])

  return useMemo(() => {
    if (!isLoggedIn || !ordersFetched || !upcomingOrders.length) {
      return undefined
    }
    const hotelOrders = upcomingOrders.filter(
      order => order.items.length > 0 || order.bedbankItems.length > 0,
    )
    const sorted = sortOrdersByDeparture(hotelOrders)
    for (const order of sorted) {
      const range = getHotelStayRangeFromOrder(order)
      if (range) {
        return { checkIn: range.start, checkOut: range.end }
      }
    }
    return undefined
  }, [isLoggedIn, ordersFetched, upcomingOrders])
}

export default useNextHotelBookingStay
